import { Points, RMWCanvasProps, ZoomSyncContext } from 'business/models/CustomModels';
import { Rectangle } from 'components/canvas/constants';
import {
  useCanvasMagnifier,
  useCanvasPanCursor,
  useRMLiteSaveImageOnRightClick,
} from 'components/canvas/hooks';
import MagnifyingRectangle from 'components/canvas/magnifyingRectangle';
import CanvasTooltip, { CanvasTooltipProps } from 'components/canvas/tooltip';
import { zoomToFocusArea } from 'components/canvas/utils';
import { useRightClick } from 'components/rightClick/rightClickProvider';
import { EKeyCodes } from 'constants/types';
import Konva from 'konva';
import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Stage, Layer } from 'react-konva';
import { ReactReduxContext, Provider } from 'react-redux';
import { DisplayModes, GetDisplayMode, isNullOrUndefined } from 'utils/Global';
import { useSelectedDrawingTool } from 'redux/hooks/well';
import { OverlayTools } from 'components/well/drawingManager/constants';

const detaultMaxScale = 20;
let maxScaleForBottomMenu = 20;
let canvasWidth = 0;
let canvasHeight = 0;
let isCanvasInFocus = false;
let isInputInFocus = false;

const defaultTooltipState = {
  windowPointerPositionX: 0,
  windowPointerPositionY: 0,
  scale: 1,
  stage: null,
  toolTipText: null,
};
let toolTipTimeoutToken = null;

const RMWCanvas = forwardRef((props: RMWCanvasProps, ref) => {
  const {
    width,
    height,
    cols,
    rows,
    maxCanvasWidthPercentage,
    canvasStageId,
    canvasStageContainerId,
    onWheel,
    initialStageScale,
    isZoomLocked,
    wellSelectionContent,
    minZoomLevel,
    onDragLeave = () => {},
    fixedContent,
    cursorClass,
    getDragBounds,
    getZoomPosition,
    onPanStart,
    onPan,
  } = props;
  const [tooltipData, setTooltipData] = useState<CanvasTooltipProps>(defaultTooltipState);
  const { handleRightClick } = useRightClick();
  const selectedDrawingTool = useSelectedDrawingTool();
  const isPanMode = selectedDrawingTool === OverlayTools.PAN;
  const [maxScale, setMaxScale] = useState(20);
  const [stageScale, setStageScale] = useState(
    isNullOrUndefined(props.stage) ? 1 : props.stage.scaleX(),
  );
  const rects = [];
  const stageX = isNullOrUndefined(props.stage) ? 0 : props.stage.x();
  const stageY = isNullOrUndefined(props.stage) ? 0 : props.stage.y();
  const [refresh, setRefresh] = useState(false);

  const cellWidth = width / rows;
  const canvasRef = useRef<HTMLInputElement>();
  const stageRef = useRef<Konva.Stage>();
  const layerRef = useRef<Konva.Layer>();
  const layerRef2 = useRef<Konva.Layer>();
  const errorLayerIconRef = useRef<Konva.Layer>();
  const errorLayerRef = useRef<Konva.Layer>();
  const fixedLayerRef = useRef<Konva.Layer>(null);
  const injectRMLiteSaveImageImage = useRMLiteSaveImageOnRightClick(
    stageRef,
    layerRef,
    fixedLayerRef,
  );

  const handleZoomSelectedArea = (rect: Rectangle) => {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    const previousScale = stage.scaleX() || 1;
    zoomToFocusArea(stage, rect, 0, 0, ({ newScale }) => {
      setStageScale(newScale);
      if (props.onZoomChange) {
        props.onZoomChange(stage, previousScale, newScale, true);
      }
    });
  };

  const {
    onMouseDown: canvasMagnifierOnMouseDown,
    onMouseMove: canvasMagnifierOnMouseMove,
    onMouseUp: canvasMagnifierOnMouseUp,
    selectionRectangle,
  } = useCanvasMagnifier(stageRef, handleZoomSelectedArea);
  const {
    onDragStart: panCursorOnDragStart,
    onDragMove: panCursorOnDragMove,
    onDragEnd: panCursorOnDragEnd,
    onMouseDown: panCursorOnMouseDown,
    onMouseUp: panCursorOnMouseUp,
  } = useCanvasPanCursor(stageRef, canvasRef, isPanMode);
  const scaleBy = 1.3;

  useEffect(() => {
    maxScaleForBottomMenu = maxScale;
  }, [maxScale]);

  useEffect(() => {
    canvasWidth = props.canvasWidth;
    canvasHeight = props.canvasHeight;
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    const oldScale = stage.scaleX();

    if (oldScale <= 1 && oldScale < canvasWidth / width) {
      let newScale = canvasWidth / width;
      if (newScale > 1) {
        newScale = 1;
      }
      stage.scale({ x: newScale, y: newScale });
      stage.position({ x: 0, y: 0 });
    }
    /**
     * This section is to adjust canvas size & position when sidebar opens or closes.
     */
    if (stage && maxCanvasWidthPercentage < 100 && oldScale <= 1) {
      /**
       * Best fit the canvas to current width of window. which is 100% right now
       */
      ZoomHandler(0);
    }

    setRefresh((prev) => !prev);
  }, [props.canvasWidth, props.canvasHeight]);

  useEffect(() => {
    stageRef?.current?.batchDraw();
  }, [rects]);

  function UpdateZoomSettings(stageCurrent, stageAtribs, zoomIn) {
    // Guard against invalid/zero scale (can happen if "best fit" is clicked
    // while canvas sizing is not ready yet, resulting in NaN/0 scale and
    // permanently broken zoom interactions).
    const oldScale = stageAtribs?.scaleX > 0 ? stageAtribs.scaleX : 1;
    const fromPos: Points = { x: stageAtribs.x ?? 0, y: stageAtribs.y ?? 0 };
    const centerX = stageAtribs.width / 2;
    const centerY = stageAtribs.height / 2;
    const zoomRefPointTo = {
      x: (centerX - stageAtribs.x) / oldScale,
      y: (centerY - stageAtribs.y) / oldScale,
    };

    let newScale = 1; // zoom fit
    if (zoomIn < 0) {
      newScale = oldScale / scaleBy;
      if (newScale < minZoomLevel) {
        newScale = minZoomLevel;
      }
    } else if (zoomIn > 0) {
      newScale = oldScale * scaleBy; // Zoom In
      if (newScale > maxScaleForBottomMenu) {
        newScale = maxScaleForBottomMenu;
      }
    }

    if (
      zoomIn === 0 ||
      (newScale < canvasWidth / width && zoomIn !== 1 && newScale < 1)
    ) {
      newScale = canvasWidth > 0 && width > 0 ? canvasWidth / width : 1;
      if (newScale > 1) {
        newScale = 1;
      }
    }
    const zoomedPos: Points = {
      x: centerX - zoomRefPointTo.x * newScale,
      y: centerY - zoomRefPointTo.y * newScale,
    };

    const syncContext: ZoomSyncContext = {
      fromPos,
      toPos: zoomedPos,
      fromScale: oldScale,
    };
    let newPos = getZoomPosition?.(newScale, syncContext);
    if (!newPos) {
      newPos = { ...zoomedPos };
      if (newScale <= 1) {
        newPos = {
          x: 0,
          y: 0,
        };
      }
      if (newPos.x > 0) {
        newPos.x = 0;
      }
      if (newPos.y > 0) {
        newPos.y = 0;
      }
    }
    stageCurrent.scale({ x: newScale, y: newScale });
    stageCurrent.position(newPos);
    stageCurrent.batchDraw();
    return { oldScale, newScale, syncContext };
  }

  function updateCanvas(
    e,
    bestFit = false,
    oldScale = null,
    newScale = null,
    syncContext: ZoomSyncContext = null,
  ) {
    const stage = stageRef.current;
    if (isNullOrUndefined(stage)) {
      return;
    }
    let end = stage.x() + width * newScale;
    // cutting from right
    if (end > window.innerWidth) {
      end = window.innerWidth;
    }

    // cutting from left
    let start = stage.x();
    if (stage.x() < 0) {
      start = 0;
    }

    const startofCol = stage.x();

    let row = stage.y() + height * newScale;
    if (row > window.innerHeight) {
      row = window.innerHeight;
    }

    const startcol = Math.round(
      (cols -
        (width * newScale - startofCol * (startofCol < 0 ? -1 : 0)) /
          newScale /
          cellWidth) *
        (startofCol < 0 ? 1 : 0),
    );
    let endcol = Math.round(startcol + (end - start) / newScale / cellWidth);
    if (endcol > cols) {
      endcol = cols;
    }

    if (props.onZoomChange !== undefined && props.onZoomChange) {
      props.onZoomChange(stage, oldScale, newScale, bestFit, syncContext);
      setStageScale(newScale);
    }
  }

  function ZoomHandler(zoomIn) {
    const stageCurrent = stageRef.current;
    if (!stageCurrent) {
      return;
    }
    // Avoid calculations when canvas hasn't been sized yet.
    if (
      !width ||
      !height ||
      width <= 0 ||
      height <= 0 ||
      !canvasWidth ||
      canvasWidth <= 0
    ) {
      return;
    }
    const stageAtribs = stageCurrent.attrs;
    const { oldScale, newScale, syncContext } = UpdateZoomSettings(
      stageCurrent,
      stageAtribs,
      zoomIn,
    );
    updateCanvas(stageAtribs, zoomIn === 0, oldScale, newScale, syncContext);
  }

  useEffect(() => {
    setMaxScale(detaultMaxScale);
    if (stageRef.current) {
      stageRef.current.batchDraw();
    }
    if (props.setZoomInvocker) {
      props.setZoomInvocker(ZoomHandler);
    }
    if (props.stageRef) {
      props.stageRef(
        stageRef.current,
        layerRef.current, //** Commented on Purpose, we might need to use thi later
        layerRef2.current,
        errorLayerRef,
        errorLayerIconRef,
      );
    }
    if (props.setZoomInvocker) {
      props.setZoomInvocker(ZoomHandler);
    }
  }, [props.expId, width, height, props.canvasWidth]);

  useImperativeHandle(ref, () => ({
    ZoomHandler,
  }));

  function handleWheel(e) {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    const oldScale = stage.scaleX() > 0 ? stage.scaleX() : 1;
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    let newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    if (oldScale >= maxScale && newScale > oldScale) {
      return;
    }
    if (newScale < 1 && newScale < canvasWidth / width) {
      newScale = canvasWidth / width;
      if (newScale > 1) {
        newScale = 1;
      }
    }
    if (newScale < minZoomLevel) {
      newScale = minZoomLevel;
    }
    if (!newScale || newScale <= 0) {
      newScale = 1;
    }
    const zoomedPos: Points = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    const fromPos: Points = { x: stage.x(), y: stage.y() };

    const syncContext: ZoomSyncContext = {
      fromPos,
      toPos: zoomedPos,
      fromScale: oldScale,
    };
    let newPos = getZoomPosition?.(newScale, syncContext);
    if (!newPos) {
      newPos = { ...zoomedPos };
      if (newScale <= 1) {
        newPos = {
          x: 0,
          y: 0,
        };
      }
      if (newPos.x > 0) {
        newPos.x = 0;
      }
      if (newPos.y > 0) {
        newPos.y = 0;
      }
    }

    if (stage) {
      stage.to({
        scaleX: newScale,
        scaleY: newScale,
        x: newPos.x,
        y: newPos.y,
        duration: 0, // duration in seconds, adjust as needed
      });
    }
    props.onZoomChange(stage, oldScale, newScale, false, syncContext);
  }

  function dragBoundFunc(pos) {
    const stage = stageRef.current;
    const scale = stage.scaleX();

    const overlayBounds = getDragBounds?.(scale);
    if (overlayBounds) {
      return {
        x: Math.max(overlayBounds.minX, Math.min(overlayBounds.maxX, pos.x)),
        y: Math.max(overlayBounds.minY, Math.min(overlayBounds.maxY, pos.y)),
      };
    }

    if (scale === 1) {
      return { x: 0, y: 0 };
    }
    if (pos.x > 0) {
      pos.x = 0;
    }
    if (pos.y > 0) {
      pos.y = 0;
    }

    const stageWidth = stage.width();
    const stageHeight = stage.height();
    const scaledWidth = width * scale;
    const scaledHeight = height * scale;

    if (scaledWidth > stageWidth) {
      const minX = stageWidth - scaledWidth;
      if (pos.x < minX) {
        pos.x = minX;
      }
    } else {
      pos.x = 0;
    }

    if (scaledHeight > stageHeight) {
      const minY = stageHeight - scaledHeight;
      if (pos.y < minY) {
        pos.y = minY;
      }
    } else {
      pos.y = 0;
    }
    return pos;
  }

  useEffect(() => {
    const handleCanvasClick = (event) => {
      const target = event.target;
      isCanvasInFocus = false;
      isInputInFocus = false;
      if (target instanceof HTMLCanvasElement) {
        isCanvasInFocus = true;
      } else if (target.nodeName == 'INPUT' || target.nodeName == 'TEXTAREA') {
        isInputInFocus = true;
      }
    };
    document.addEventListener('mousedown', handleCanvasClick);
    return () => {
      window.removeEventListener('mousedown', handleUserKeyPress);
      layerRef2.current = null;
      stageRef.current = null;
      errorLayerIconRef.current = null;
      errorLayerRef.current = null;
    };
  }, [canvasRef]);

  function onDragEnd(e) {
    const scale = stageRef.current.scaleX();
    const pos: Points = stageRef.current.position();
    updateCanvas(e, false, scale, scale, { fromPos: pos, toPos: pos, fromScale: scale });
    panCursorOnDragEnd();
  }
  function onDragStart() {
    onPanStart?.();
    panCursorOnDragStart();
  }

  function onDragMove() {
    onPan?.();
    panCursorOnDragMove();
  }

  function onClick(evt) {
    if (props.onClick !== undefined) {
      props.onClick(evt);
    }
  }

  function onDblClick(evt) {
    if (props.onDblClick !== undefined) {
      props.onDblClick(evt);
    }
  }

  function onDragOver(event) {
    event.preventDefault();
    if (props?.onDragOver) {
      props.onDragOver(event);
    }
  }

  function onMouseDown(evt) {
    if (props.onMouseDown !== undefined) {
      props.onMouseDown(evt);
    }
    panCursorOnMouseDown(evt);
    canvasMagnifierOnMouseDown(evt);
  }

  function onMouseEnter(evt) {
    if (props.onMouseEnter !== undefined) {
      props.onMouseEnter(evt);
    }
  }

  function onMouseLeave(evt) {
    if (props.onMouseLeave !== undefined) {
      props.onMouseLeave(evt);
    }
  }

  function onMouseUp(evt) {
    if (props.onMouseUp !== undefined) {
      props.onMouseUp(evt);
    }
    panCursorOnMouseUp(evt);
    const isMagnifying = canvasMagnifierOnMouseUp(evt);
    if (!isMagnifying && evt.evt.button === 2) {
      injectRMLiteSaveImageImage();
      handleRightClick(evt.evt);
    }
  }

  function onMouseMove(evt) {
    if (props.onMouseMove !== undefined) {
      props.onMouseMove(evt);
    }
    canvasMagnifierOnMouseMove(evt);

    evt.evt.preventDefault();
    setTooltipData(defaultTooltipState);
    if (toolTipTimeoutToken) {
      clearTimeout(toolTipTimeoutToken);
    }
    toolTipTimeoutToken = setTimeout(() => {
      if (evt.target.attrs.tooltipInfo) {
        const { x, y } = evt.target.getStage().getRelativePointerPosition();
        setTooltipData({
          windowPointerPositionX: x,
          windowPointerPositionY: y,
          scale: 1,
          stage: null,
          toolTipText: evt.target.attrs.tooltipInfo,
        });
      } else {
        tooltipData.toolTipText !== null && setTooltipData(defaultTooltipState);
      }
      toolTipTimeoutToken = null;
    }, 1000);
  }

  function onDrop(event, cat) {
    const stage = stageRef.current;
    const stagecontainer = document.getElementById('stagecontainer');

    if (!stage || !stagecontainer) {
      if (props.onDrop) {
        props.onDrop(event);
      }
      return;
    }
    const scale = stage.scaleX();
    const stageX = stage.x();
    const stageY = stage.y();

    const offSetleft = stagecontainer.getBoundingClientRect().left;
    const offSettop = stagecontainer.getBoundingClientRect().top;

    const { clientX } = event;
    const { clientY } = event;

    const isvalidX = clientX - (stageX + offSetleft);
    const isvalidY = clientY - (stageY + offSettop);

    const endX = stage.x() + width * scale + offSetleft;

    const endY = stage.y() + height * scale + offSettop;

    if (isvalidX < 0 || isvalidY < 0 || clientX > endX || clientY > endY) {
      return;
    }

    if (props.onDrop) {
      props.onDrop(event);
    }
  }

  function handleUserKeyPress(event) {
    const isArrowKey =
      event.keyCode === EKeyCodes.Left ||
      event.keyCode === EKeyCodes.Right ||
      EKeyCodes.Down ||
      event.keyCode === EKeyCodes.Up;

    if ((isCanvasInFocus || (isArrowKey && !isInputInFocus)) && props.onKeyPress) {
      props.onKeyPress(event);
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);

    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
      if (toolTipTimeoutToken) {
        clearTimeout(toolTipTimeoutToken);
      }
    };
  }, [handleUserKeyPress]);

  if (refresh) {
    setRefresh(!refresh);
  }

  useEffect(() => {
    const stage = stageRef.current;
    const fixedLayer = fixedLayerRef.current;
    if (!stage || !fixedLayer) {
      return;
    }

    const updateFixedLayer = () => {
      const scaleX = stage.scaleX() || 1;
      const scaleY = stage.scaleY() || 1;
      const pos = stage.position();

      fixedLayer.x(-pos.x / scaleX);
      fixedLayer.y(-pos.y / scaleY);
      fixedLayer.scale({ x: 1 / scaleX, y: 1 / scaleY });

      fixedLayer.batchDraw();
    };

    updateFixedLayer();

    stage.on('xChange yChange scaleXChange scaleYChange', updateFixedLayer);

    return () => {
      stage.off('xChange yChange scaleXChange scaleYChange', updateFixedLayer);
    };
  }, [fixedContent]);

  return (
    <div
      id={canvasStageContainerId ? canvasStageContainerId : 'stagecontainer'}
      className="droppable"
      onDragOver={(event) => onDragOver(event)}
      onDrop={(event) => onDrop(event, 'Done')}
      onDragLeave={onDragLeave}
      style={{
        margin: 'auto',
        marginLeft: 5,
      }}
      ref={canvasRef}
    >
      {/* Need to pass store again as Konva has a bug */}
      {/* https://github.com/konvajs/react-konva/issues/311 */}
      <ReactReduxContext.Consumer>
        {({ store }) => (
          <Stage
            ref={stageRef}
            style={{
              marginLeft: 5,
            }}
            className={!isPanMode && cursorClass}
            key={canvasStageId ? canvasStageId : 'stage'}
            id={canvasStageId ? canvasStageId : 'stage'}
            width={canvasWidth || window.innerWidth}
            height={canvasHeight || window.innerHeight}
            scaleX={initialStageScale || stageScale}
            scaleY={initialStageScale || stageScale}
            x={stageX}
            y={stageY}
            onWheel={!isZoomLocked ? onWheel || handleWheel : null}
            onClick={onClick}
            onDblClick={onDblClick}
            draggable={!props.isDrawingActive ? props.draggable : false}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            onDragMove={!props.isDrawingActive ? onDragMove : null}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            dragBoundFunc={dragBoundFunc}
            onDragOver={props?.onDragOver}
            onDrop={props?.onDrop}
          >
            <Provider store={store} key={'rmw_stage'}>
              <Layer id={`rmwcanvas-layer1`} ref={layerRef}>
                {props.children}
              </Layer>
              {/* TODO. this does not allow design mode canvas wells selectable. ideally draw invisible rects instead */}

              <Layer id={`rmwcanvas-layer2`} ref={layerRef2}></Layer>
              <Layer
                id={`rmwcanvas-layer-error-icon`}
                ref={errorLayerIconRef}
                visible={GetDisplayMode() === DisplayModes.DESIGN}
              ></Layer>
              <Layer id={`rmwcanvas-layer-error`} ref={errorLayerRef}></Layer>
              <Layer id="konva-layer-well-selection">{wellSelectionContent}</Layer>
              <Layer
                id={`rmwcanvas-layer-showscores`}
                opacity={props.showScoresToolTip === false ? 0 : 1}
              >
                <CanvasTooltip
                  {...tooltipData}
                  scale={stageRef?.current?.scaleX() || 1}
                  stage={stageRef?.current}
                />
              </Layer>
              <MagnifyingRectangle magnifyingRectangle={selectionRectangle} />
              {fixedContent && (
                <Layer id={`rmwcanvas-fixedlayer`} ref={fixedLayerRef}>
                  {fixedContent}
                </Layer>
              )}
            </Provider>
          </Stage>
        )}
      </ReactReduxContext.Consumer>
    </div>
  );
});
export default RMWCanvas;
